import { Table, TablePaginationConfig } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { AxiosError } from 'axios'
import { t } from 'i18next'
import moment from 'moment'
import { MouseEvent, useCallback, useMemo, useState } from 'react'
import Moment from 'react-moment'
import { useNavigate } from 'react-router-dom'
import {
  ConfirmDeleteModal,
  DeleteIconButton,
  ListPageHeader,
  ListPageLayout,
  PageContentDropdown,
  SectionLoadingState,
  TableBoldColumnContainer,
} from '../../components'
import {
  DEFAULT_PAGE_SIZE,
  NO_DATA_PLACEHOLDER,
  backEndSortOrders,
  dateFormat,
  sorterTypeDictionary,
  urlList,
} from '../../constants/constants'
import { useMediaQuery, useWorkspaces } from '../../hooks'
import { apiService, notificationService } from '../../services'
import { Workspace } from '../../store/reducers/workspaceReducer'
import { routePaths } from '../RootPage'
import CreateWorkspaceModal from './modals/CreateWorkspaceModal'
import { FilterValue, SorterResult } from 'antd/lib/table/interface'

interface ExtendedWorkspace extends Workspace {
  key: string
}

const WorkspaceListPage = () => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortOrder, setSortOrder] = useState(backEndSortOrders.ASC)
  const [sortField, setSortField] = useState<string>('title')

  const {
    workspaceList,
    totalResults,
    loading,
    refreshWorkspaceList,
    exportWorkspaceListCsv,
    isCreatable,
    isDeletable,
  } = useWorkspaces({
    params: {
      page: page.toString(),
      pageSize: pageSize.toString(),
      sortOrder,
      sortField,
    },
  })

  const isMobile = useMediaQuery('xs')

  const navigate = useNavigate()
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const formattedData: ExtendedWorkspace[] =
    useMemo(
      () =>
        workspaceList?.map((workspace: Workspace) => ({
          ...workspace,
          key: workspace.id,
        })),
      [workspaceList]
    ) || []

  const handleClick = useCallback(
    (id: string) => {
      const selectedWorkspace = workspaceList?.find(
        (workspace) => workspace.id === id
      )
      if (selectedWorkspace) {
        navigate(`${routePaths.WORKSPACE_DETAILS}/${selectedWorkspace.id}`)
      }
    },
    [workspaceList, navigate]
  )

  const handleDeleteClick = useCallback(
    (event: MouseEvent, id: string) => {
      event.stopPropagation()
      setDeleteId(id)
      setConfirmDeleteModalOpen(true)
    },
    [setDeleteId, setConfirmDeleteModalOpen]
  )

  const handleConfirmDelete = useCallback(() => {
    if (deleteId) {
      setDeleteLoading(true)
      apiService
        .deleteItem(`${urlList.WORKSPACES}/${deleteId}`, navigate)
        .then((response: Workspace) => {
          refreshWorkspaceList()
          setDeleteLoading(false)
          notificationService.notificationSuccess({
            messageToDisplay: t('workspaceListPage.deletedWorkspaceSuccessful'),
          })
        })
        .catch((error: AxiosError | Error) => {
          console.error('axios fetch error', error)
          setDeleteLoading(false)
        })
    }
    setConfirmDeleteModalOpen(false)
    setDeleteId(undefined)
  }, [deleteId, refreshWorkspaceList, navigate])

  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<Workspace> | SorterResult<Workspace>[]
    ) => {
      setPage(pagination.current || 1)
      if (pagination.pageSize && pagination.pageSize !== pageSize) {
        setPageSize(pagination.pageSize)
      }

      const isSortArray = Array.isArray(sorter)
      const firstSort = isSortArray ? sorter[0] : sorter

      setSortOrder(sorterTypeDictionary[firstSort.order || 'ascend'])
      setSortField(firstSort.field as string)
    },
    [pageSize]
  )

  const columns: ColumnsType<Workspace> = useMemo(
    () => [
      {
        title: t('Title'),
        dataIndex: 'title',
        key: 'title',
        width: '45%',
        sorter: (a: Workspace, b: Workspace) =>
          a.title && b.title ? a.title.localeCompare(b.title) : -1,
        render: (text) => (
          <TableBoldColumnContainer>
            {text || NO_DATA_PLACEHOLDER}
          </TableBoldColumnContainer>
        ),
      },
      {
        title: t('Workspace Type'),
        dataIndex: 'typeName',
        key: 'workspaceType',
        width: '15%',
        sorter: (a: Workspace, b: Workspace) =>
          a.typeName && b.typeName ? a.typeName.localeCompare(b.typeName) : -1,
        render: (text) => <>{text || NO_DATA_PLACEHOLDER}</>,
      },
      {
        title: t('Privacy'),
        dataIndex: 'visibility',
        key: 'privacy',
        render: (privacy: string) => <>{t(privacy) || NO_DATA_PLACEHOLDER}</>,
      },
      {
        title: t('Last Update'),
        dataIndex: 'lastModifiedDate',
        key: 'lastModifiedDate',
        sorter: (a: Workspace, b: Workspace) =>
          a.lastModifiedDate && b.lastModifiedDate
            ? moment(a.lastModifiedDate).valueOf() -
              moment(b.lastModifiedDate).valueOf()
            : -1,
        render: (lastModifiedDate) => (
          <>
            {lastModifiedDate ? (
              <Moment local format={dateFormat.PRIMARY}>
                {lastModifiedDate}
              </Moment>
            ) : (
              NO_DATA_PLACEHOLDER
            )}
          </>
        ),
      },
      {
        title: '',
        key: 'action',
        align: 'right',
        render: (text, { id }) => (
          <>
            {isDeletable && (
              <DeleteIconButton
                tooltipTitle={t('workspaceListPage.deleteWorkspaceTooltip')}
                onClick={(e: MouseEvent) => handleDeleteClick(e, id)}
              />
            )}
          </>
        ),
      },
    ],
    [isDeletable, handleDeleteClick]
  )

  const handleWorkspaceListExport = useCallback(() => {
    exportWorkspaceListCsv()
  }, [exportWorkspaceListCsv])

  return loading ? (
    <SectionLoadingState />
  ) : (
    <>
      <ListPageLayout>
        <ListPageHeader
          title={t('workspaceListPage.title', { count: totalResults })}
          actions={
            <>
              <CreateWorkspaceModal disabled={!isCreatable} />
              <PageContentDropdown
                onExport={handleWorkspaceListExport}
                disabled={formattedData.length === 0}
              />
            </>
          }
        />

        <Table
          rowClassName="page-list-table-row clickable"
          columns={columns}
          dataSource={formattedData}
          onChange={handleTableChange}
          pagination={{
            current: page,
            total: totalResults,
            pageSize: pageSize,
            position: ['bottomLeft'],
            showSizeChanger: false,
          }}
          onRow={(record) => ({
            onClick: () => handleClick(record.id),
          })}
          scroll={{
            x: isMobile ? 'max-content' : '100%',
            y: 'calc(100vh - 155px)',
          }}
        />
      </ListPageLayout>
      <ConfirmDeleteModal
        open={confirmDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteModalOpen(false)}
        loading={deleteLoading}
      >
        {t('workspaceListPage.confirmDeleteModalContent')}
      </ConfirmDeleteModal>
    </>
  )
}

export default WorkspaceListPage
